import { UIState, UIAction, UIActionTypes } from '../../types/gameUI';
import { deepMerge } from 'game/store/utils';

const initialState: UIState = {
  gameStats: {
    difficulty: 'Hard',
    moves: 0,
    time: 0,
    leftX: 5,
    rightX: 5,
  },
  modals: {
    designModal: {
      isOpen: false,
      activeTab: 'patterns'
    },
    settingsModal: {
      isOpen: false
    },
    missionsModal: {
      isOpen: false,
      weeklyDisabled: false
    },
    missionCompletePopup: {
      placement: 'winscreen',
      isOpen: false
    },
    storiesModal: {
      isOpen: false,
      stories: []
    },
    imagePreviewModal: {
      isOpen: false,
      images: [],
      initialSlideIndex: 0,
      storyName: ''
    },
    newGameModal: {
      isOpen: false,
    }
  },
  settings: [],
  design: {
    selectedPattern: '1_blue',
    selectedCardTheme: '1_white',
    selectedBackground: 'default'
  },
  indicators: {
    shareIndicator: false,
    hintsIndicator: false,
    hintsCount: 0,
    settingsIndicator: false
  },
  buttons: {
    undoDisabled: true,
    hintsDisabled: false,
    magicWandDisabled: false,
    storiesDisabled: false,
    showMagicWand: true,
    showAutocomplete: false,
    hideDesignFirstTab: false,
    magicWandsCount: 1
  },
  labels: {
    movesLabel: 'Moves',
    timeLabel: 'Time',
    settingsText: 'Settings',
    feedbackText: 'Feedback',
    designText: 'Design',
    autocompleteText: 'Autocomplete'
  },
  isExpanded: false,
  tutorialActive: false,
  isInitialized: false,
  missions: {
    dailyMission: undefined,
    isNewMissionCompleted: false
  }
};

export const gameUIReducer = (state = initialState, action: UIAction): UIState => {
  switch (action.type) {
    case UIActionTypes.UPDATE_UI_STATE:
      return deepMerge(state, action.payload);

    case UIActionTypes.OPEN_DESIGN_MODAL:
      
      return {
        ...state,
        modals: {
          ...state.modals,
          designModal: {
            isOpen: true,
            activeTab: action.payload || state.modals.designModal.activeTab
          },
          settingsModal: {
            ...state.modals.settingsModal,
            isOpen: false
          }
        },
        isExpanded: true
      };

    case UIActionTypes.CLOSE_DESIGN_MODAL:
      return {
        ...state,
        modals: {
          ...state.modals,
          designModal: {
            ...state.modals.designModal,
            isOpen: false
          }
        }
      };

    case UIActionTypes.SET_DESIGN_TAB:
      return {
        ...state,
        modals: {
          ...state.modals,
          designModal: {
            ...state.modals.designModal,
            activeTab: action.payload
          }
        }
      };

    case UIActionTypes.OPEN_SETTINGS_MODAL:
      
      return {
        ...state,
        modals: {
          ...state.modals,
          settingsModal: {
            isOpen: true
          },
          designModal: {
            ...state.modals.designModal,
            isOpen: false
          }
        },
        isExpanded: true
      };

    case UIActionTypes.CLOSE_SETTINGS_MODAL:
      return {
        ...state,
        modals: {
          ...state.modals,
          settingsModal: {
            isOpen: false
          }
        },
        isExpanded: false
      };

    case UIActionTypes.CLOSE_MISSIONS_MODAL:
      return {
        ...state,
        modals: {
          ...state.modals,
          missionsModal: {
            ...state.modals.missionsModal,
            isOpen: false
          }
        },
        isExpanded: false
      };

    case UIActionTypes.TOGGLE_IS_NEW_MISSION:
      return {
        ...state,
        missions: {
          ...state.missions,
          isNewMissionCompleted: action.payload,
          isNewMissionCompletedOnMounted: action.payload
        }
      };

    case UIActionTypes.CLOSE_ALL_PANELS:
      return {
        ...state,
        modals: {
          ...state.modals,
          settingsModal: {
            isOpen: false
          },
          designModal: {
            ...state.modals.designModal,
            isOpen: false
          },
          missionsModal: {
            ...state.modals.missionsModal,
            isOpen: false
          }
        },
        isExpanded: false
      };

    case UIActionTypes.TOGGLE_SETTING:
      
      
      
      return {
        ...state,
        settings: state.settings.map(setting => 
          setting.key === action.payload 
            ? { ...setting, checked: !setting.checked }
            : setting
        )
      };

    case UIActionTypes.OPEN_MISSION_COMPLETE_POPUP:
      return {
        ...state,
        modals: {
          ...state.modals,
          missionCompletePopup: {
            placement: action.payload,
            isOpen: true
          }
        }
      };

    case UIActionTypes.CLOSE_MISSION_COMPLETE_POPUP:
      return {
        ...state,
        modals: {
          ...state.modals,
          missionCompletePopup: {
            ...state.modals.missionCompletePopup,
            isOpen: false
          }
        }
      };

    case UIActionTypes.UPDATE_SETTINGS:
      
      return {
        ...state,
        settings: action.payload
      };

    case UIActionTypes.SHOW_IMAGE_PREVIEW_MODAL:

      return {
        ...state,
        modals: {
          ...state.modals,
          imagePreviewModal: {
            ...state.modals.imagePreviewModal,
            isOpen: true,
            placement: action.payload.placement,
            storyName: action.payload.storyName,
            images: action.payload.images,
            initialSlideIndex: action.payload.initialSlideIndex,
          }
        }
      };

    case UIActionTypes.CLOSE_IMAGE_PREVIEW_MODAL:

      return {
        ...state,
        modals: {
          ...state.modals,
          imagePreviewModal: {
            ...state.modals.imagePreviewModal,
            isOpen: false,
            images: [],
            initialSlideIndex: 0
          }
        }
      };

    case UIActionTypes.OPEN_NEW_GAME_MODAL:
      return {
        ...state,
        modals: {
          ...state.modals,
          newGameModal: { isOpen: true },
          settingsModal: { isOpen: false },
          designModal: { ...state.modals.designModal, isOpen: false },
          missionsModal: { ...state.modals.missionsModal, isOpen: false },
          storiesModal: { ...state.modals.storiesModal, isOpen: false },
        },
        isExpanded: true
      };

    case UIActionTypes.CLOSE_NEW_GAME_MODAL:
      return {
        ...state,
        modals: {
          ...state.modals,
          newGameModal: { isOpen: false },
        },
        isExpanded: false
      };

    case UIActionTypes.SET_CURRENT_DIFFICULTY:
      return {
        ...state,
        modals: { ...state.modals, newGameModal: { isOpen: !!state.modals.newGameModal?.isOpen, currentDifficulty: action.payload } }
      };

    default:
      return state;
  }
}; 